import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import TweetCard from './TweetCard';

const TweetCarousel = ({ tweets, batchSize = 4, interval = 5000 }) => {
  const [currentBatch, setCurrentBatch] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isFlashing, setIsFlashing] = useState(false);
  const containerRef = useRef(null);

  // 把 tweets 分成每批 batchSize 个
  const batches = [];
  for (let i = 0; i < tweets.length; i += batchSize) {
    batches.push(tweets.slice(i, i + batchSize));
  }

  const totalBatches = batches.length;

  // 复制第一批到最后，实现无缝循环
  const displayBatches = totalBatches > 1 ? [...batches, batches[0]] : batches;

  useEffect(() => {
    if (totalBatches <= 1 || isPaused) return;

    const timer = setInterval(() => {
      // 先闪烁
      setIsFlashing(true);
      setTimeout(() => {
        setIsFlashing(false);
        setCurrentBatch((prev) => prev + 1);
      }, 300);
    }, interval);

    return () => clearInterval(timer);
  }, [totalBatches, interval, isPaused]);

  // 当滑到复制的第一批时，瞬间跳回真正的第一批
  useEffect(() => {
    if (currentBatch === totalBatches && totalBatches > 1) {
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentBatch(0);
        // 下一帧恢复动画
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        });
      }, 600);
    }
  }, [currentBatch, totalBatches]);

  if (tweets.length === 0) return null;

  return (
    <Box
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%'
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          transition: isTransitioning ? 'transform 0.6s ease-in-out' : 'none',
          transform: `translateX(-${currentBatch * 100}%)`,
        }}
      >
        {displayBatches.map((batch, batchIdx) => (
          <Box
            key={batchIdx}
            sx={{
              minWidth: '100%',
              width: '100%',
              flexShrink: 0,
              position: 'relative',
              '&::after': batchIdx === currentBatch && isFlashing ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(99, 102, 241, 0.15)',
                borderRadius: 2,
                animation: 'flash 0.3s ease-out',
                pointerEvents: 'none',
                '@keyframes flash': {
                  '0%': { opacity: 0 },
                  '50%': { opacity: 1 },
                  '100%': { opacity: 0 },
                },
              } : {},
            }}
          >
            {batch.map((tweet, idx) => (
              <TweetCard key={`${tweet.post_id}-${batchIdx}-${idx}`} postId={tweet.post_id} />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TweetCarousel;
